import {
  Component,
  computed,
  EventEmitter,
  input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { QuestionModel } from '../../api/models/question-model';
import { AnswerModel, QuestionType } from '../../api/models';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FileService } from '../../api/services/file.service';
import { AuthService } from '../../services/auth.service';
import { saveAs } from 'file-saver';
import { AttachmentModel } from '../../api/models/attachment-model';

@Component({
  selector: 'app-question',
  templateUrl: './question.component.html',
  styleUrls: ['question.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class QuestionComponent implements OnChanges, OnDestroy {
  question = input.required<QuestionModel>();
  @Output() autoAdvanceSingleChoice = new EventEmitter<void>();

  isMobile = signal(window.innerWidth < 640);

  singleSelectOptionGuid: string = '';
  multiSelectOptionGuids: string[] = [];
  freeText: string = '';
  rangeUpperBound: number = 0;
  rangeSelectionTouched = false;

  canMoveToNextQuestion = false;

  /**
   * File extensions which can be displayed as images, rather than downloadable links
   */
  imageFileExtensions: string[] = [
    '.jpeg',
    '.jpg',
    '.gif',
    '.png',
    'bmp',
    'tiff',
    'svg',
  ];

  /**
   * Attachments which are not images, and so should be displayed as download links
   */
  downloadableAttachments: AttachmentModel[] = [];

  /**
   * Attachments which are images
   */
  imageUrls: string[] = [];
  private imageUrlGeneration = 0;

  constructor(
    private fileService: FileService,
    private authService: AuthService,
  ) {
    console.info('QuestionComponent constructed');
  }

  ngOnChanges() {
    console.info('ngOnChanges triggered with question:', this.question());

    if (!this.question()) {
      console.warn('No question provided to QuestionComponent');
      return;
    }

    console.info('Processing ngOnChanges for question:', this.question());
    this.divideQuestionAttachments();
    this.setExistingAnswerData();
    this.updateUserCanMoveToNextQuestion();
    console.info('ngOnChanges processing completed');
  }

  divideQuestionAttachments() {
    this.downloadableAttachments = [];
    this.resetImageUrls();
    const imageUrlGeneration = this.imageUrlGeneration;

    // Divide attachments into images and non-images
    for (const attachment of this.question().attachments) {
      if (this.imageFileExtensions.some((ex) => attachment.name.endsWith(ex))) {
        this.downloadImage(attachment, imageUrlGeneration);
      } else {
        this.downloadableAttachments.push(attachment);
      }
    }
  }

  getMaxTextAreaLength = computed(() => {
    // TODO : Include ShortFreeText, LongFreeText etc types in KQ ?
    return this.question().type === QuestionType.FreeText ? 500 : 0;
  });

  getRows = computed(() => {
    // TODO : Include ShortFreeText, LongFreeText etc types in KQ ?
    return this.question().type === QuestionType.FreeText ? 3 : 0;
  });

  getProgressBarWidth = computed(() => {
    return `${(this.question().progress ?? 0) * 100}%`;
  });

  /**
   * Returns true if and only if there is at least one answer option with a freetext input box
   */
  questionHasInputOption = computed(() => {
    return !!this.question().answerOptions?.filter(
      (o) => o.includeFreeTextInput,
    ).length;
  });

  /*
    Update the list of multi-select guids
   */
  updateMultiSelectList(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.multiSelectOptionGuids.push(checkbox.value);
    } else {
      this.multiSelectOptionGuids.splice(
        this.multiSelectOptionGuids.indexOf(checkbox.value),
        1,
      );
    }

    this.updateUserCanMoveToNextQuestion();
  }

  onSingleChoiceOptionChange(optionGuid: string | undefined) {
    this.singleSelectOptionGuid = optionGuid ?? '';
    this.updateUserCanMoveToNextQuestion();

    const selectedOption = this.question().answerOptions?.find(
      (option) => option.guid === optionGuid,
    );

    if (
      selectedOption?.autoAdvanceOnSelect &&
      !selectedOption.includeFreeTextInput
    ) {
      this.autoAdvanceSingleChoice.emit();
    }
  }

  hasQuestionSetIntroduction(question: QuestionModel) {
    return !!question.questionSetIntroductionText?.trim();
  }

  onRangeSelectionChange() {
    this.rangeSelectionTouched = true;
    this.updateUserCanMoveToNextQuestion();
  }

  /*
    Generates an answer model based on the current model state
   */
  getAnswerModel() {
    const answer: AnswerModel = {};

    const question = this.question();

    switch (question.type) {
      case QuestionType.SingleChoice: {
        answer.selectedAnswerOptions = question.answerOptions
          ?.filter((ao) => ao.guid == this.singleSelectOptionGuid)
          .map((ao) => {
            return { guid: ao.guid!, freeText: ao.freeText ?? '' };
          });
        break;
      }
      case QuestionType.MultipleChoice: {
        answer.selectedAnswerOptions = question.answerOptions
          ?.filter((ao) => this.multiSelectOptionGuids.includes(ao.guid!))
          .map((ao) => {
            return { guid: ao.guid!, freeText: ao.freeText ?? '' };
          });
        break;
      }
      case QuestionType.Date:
      case QuestionType.FreeText: {
        answer.selectedAnswerOptions = [];
        answer.freeText = this.freeText;
        break;
      }
      case QuestionType.RangeSelection:
        answer.rangeLowerBound = 0;
        answer.rangeUpperBound = this.rangeUpperBound;
        answer.selectedAnswerOptions = [
          {
            guid: question.answerOptions![this.rangeUpperBound].guid!,
            freeText: '',
          },
        ];

        break;
      case QuestionType.Ranking: {
        answer.selectedAnswerOptions = question.answerOptions?.map((ao) => {
          return { guid: ao.guid!, freeText: ao.freeText ?? '' };
        });
        break;
      }
    }

    return answer;
  }

  /*
    Set the current state of the view based on the current question. This allows already selected answers to be
    pre-populated when using the Previous or Resume actions
   */
  setExistingAnswerData() {
    // Clear the existing form data
    this.clearFormData();

    // Set free text
    if (this.question().freeText) {
      this.freeText = this.question().freeText ?? '';
    }

    // Set answer options
    if (this.question().answerOptions) {
      for (const option of this.question().answerOptions?.filter(
        (o) => o.selected,
      ) || []) {
        if (this.question().type == QuestionType.SingleChoice) {
          this.singleSelectOptionGuid = option.guid!;
        } else if (this.question().type == QuestionType.MultipleChoice) {
          this.multiSelectOptionGuids.push(option.guid!);
        } else if (this.question().type == QuestionType.RangeSelection) {
          this.rangeUpperBound =
            this.question().answerOptions?.indexOf(option) ?? 0;
          this.rangeSelectionTouched = true;
        }
      }
    }
  }

  clearFormData() {
    this.freeText = '';
    this.singleSelectOptionGuid = '';
    this.multiSelectOptionGuids = [];
    this.rangeUpperBound = 0;
    this.rangeSelectionTouched = false;
  }

  /*
    Determines whether the user can move to the next question
   */
  updateUserCanMoveToNextQuestion() {
    if (this.question().isOptional) {
      this.canMoveToNextQuestion = true;
    } else {
      switch (this.question().type) {
        case QuestionType.SingleChoice: {
          if (this.singleSelectOptionGuid) {
            // If a single select answer has been selected, determine whether we can proceed
            // based on whether the answer option has freetext
            const answerOption = this.question().answerOptions?.filter(
              (o) => o.guid == this.singleSelectOptionGuid,
            )[0];

            this.canMoveToNextQuestion = !!(
              answerOption &&
              (!answerOption.includeFreeTextInput || answerOption.freeText)
            );
          } else {
            this.canMoveToNextQuestion = false;
          }

          break;
        }
        case QuestionType.MultipleChoice: {
          this.canMoveToNextQuestion = this.multiSelectOptionGuids.length > 0;
          break;
        }
        case QuestionType.Date:
        case QuestionType.FreeText: {
          this.canMoveToNextQuestion = this.freeText.length > 0;
          break;
        }
        case QuestionType.RangeSelection: {
          this.canMoveToNextQuestion = this.rangeSelectionTouched;
          break;
        }
        case QuestionType.Ranking: {
          this.canMoveToNextQuestion = true;
          break;
        }
      }
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    const answerOptions = this.question().answerOptions;
    if (answerOptions) {
      moveItemInArray(answerOptions, event.previousIndex, event.currentIndex);

      for (let i = 0; i < answerOptions.length; i++) {
        answerOptions[i].index = i;
      }
    }
  }

  /**
   * Downloads an image and saves it as a URL to be rendered in the view
   */
  private downloadImage(
    attachment: AttachmentModel,
    imageUrlGeneration: number,
  ) {
    this.getFile(attachment).subscribe({
      next: (res) => {
        const blob = new Blob([res.body as BlobPart]);
        const url = URL.createObjectURL(blob);

        if (imageUrlGeneration !== this.imageUrlGeneration) {
          URL.revokeObjectURL(url);
          return;
        }

        this.imageUrls.push(url);
      },
      error: () => {},
    });
  }

  /**
   * Downloads a file to the users browser
   */
  downloadFile(attachment: AttachmentModel) {
    this.getFile(attachment).subscribe({
      next: (res) => {
        const blob = new Blob([res.body as BlobPart]);
        saveAs(blob, attachment.name);
      },
      error: () => {},
    });
  }

  /**
   * Gets a file
   */
  private getFile(attachment: AttachmentModel) {
    return this.fileService.getFile(
      { guid: attachment.guid },
      this.authService.getAuthorisationHeaders(),
    );
  }

  ngOnDestroy(): void {
    this.resetImageUrls();
  }

  private resetImageUrls() {
    for (const imageUrl of this.imageUrls) {
      URL.revokeObjectURL(imageUrl);
    }

    this.imageUrls = [];
    this.imageUrlGeneration += 1;
  }

  protected readonly QuestionType = QuestionType;
}
